import {
  Badge,
  Box,
  Group,
  Stack,
  Text,
  Loader,
  Alert,
} from '@mantine/core';
import { IconBriefcase, IconCalendar, IconTargetArrow, IconTrendingUp, IconUser } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import type { TreatmentGoal, TreatmentPlan } from '../../clients/data/types';
import { formatUsDateShort } from '@/lib/dateHelpers';
import {
  TreatmentPlanClientSelect,
  type TreatmentPlanParticipantOption,
} from '../components/TreatmentPlanClientSelect';

interface TreatmentPlansTabProps {
  plans: TreatmentPlan[];
  loading?: boolean;
  error?: string | null;
  groupMode?: boolean;
  participantOptions?: TreatmentPlanParticipantOption[];
  selectedPatientId?: string;
  onSelectedPatientChange?: (patientId: string) => void;
  selectedPatientName?: string;
}

function planStatusColor(status: TreatmentPlan['status']): string {
  switch (status) {
    case 'active': return 'green';
    case 'completed': return 'blue';
    case 'on-hold': return 'yellow';
  }
}

function goalStatusLabel(status?: string): string {
  switch (status) {
    case 'completed': return 'Completed';
    case 'in-progress': return 'Active';
    case 'not-started': return 'Not started';
    default: return status ?? '—';
  }
}

function IconTile({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box
      style={{
        width: 32,
        height: 32,
        borderRadius: 'var(--mantine-radius-sm)',
        backgroundColor: 'var(--mantine-color-gold-1)',
        color: 'var(--mantine-color-gold-8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

function GoalsSection({ goals }: { goals: TreatmentGoal[] }): JSX.Element {
  return (
    <Stack gap="xs">
      <Group gap={6}>
        <IconTargetArrow size={14} color="var(--mantine-color-dimmed)" />
        <Text size="sm" fw={600}>Goals</Text>
      </Group>
      {goals.length === 0 ? (
        <Text size="xs" c="dimmed">No goals documented.</Text>
      ) : (
        goals.map((goal) => (
          <Group
            key={goal.id}
            justify="space-between"
            align="flex-start"
            wrap="nowrap"
            gap="sm"
            p="sm"
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              border: '1px solid var(--mantine-color-gray-3)',
            }}
          >
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }} align="flex-start">
              <IconTile>
                <IconTrendingUp size={16} />
              </IconTile>
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={600}>{goal.description}</Text>
                {goal.objectiveBlocks?.map((block) => (
                  <Stack key={block.id} gap={2} mt={4}>
                    <Text size="xs">
                      <Text span fw={600}>Objective: </Text>
                      {block.objective.description}
                    </Text>
                    {block.interventions.map((intervention, index) => (
                      <Text key={`${block.id}-intervention-${index}`} size="xs" c="dimmed">
                        <Text span fw={600}>Intervention: </Text>
                        {intervention}
                      </Text>
                    ))}
                  </Stack>
                ))}
                {goal.targetDate ? (
                  <Text size="xs" c="dimmed">Target: {goal.targetDate}</Text>
                ) : null}
              </Stack>
            </Group>
            {goal.status && (
              <Badge size="xs" variant="light" color="gold" style={{ flexShrink: 0 }}>
                {goalStatusLabel(goal.status)}
              </Badge>
            )}
          </Group>
        ))
      )}
    </Stack>
  );
}

function PlansBody({
  plans,
  loading,
  error,
  emptyMessage,
  loadingMessage,
}: {
  plans: TreatmentPlan[];
  loading: boolean;
  error: string | null;
  emptyMessage: string;
  loadingMessage: string;
}): JSX.Element {
  if (loading) {
    return (
      <Stack align="center" py="lg" gap="xs">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">{loadingMessage}</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light">
        <Text size="sm">{error}</Text>
      </Alert>
    );
  }

  const activePlans = plans.filter((p) => p.status === 'active');
  const displayPlans = activePlans.length > 0 ? activePlans : plans;
  const sorted = [...displayPlans].sort((a, b) => {
    const order = { active: 0, 'on-hold': 1, completed: 2 };
    return order[a.status] - order[b.status];
  });

  if (sorted.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="lg">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {sorted.map((plan) => (
        <Stack key={plan.id} gap="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }} align="flex-start">
              <IconTile>
                <IconBriefcase size={16} />
              </IconTile>
              <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={600}>{plan.program}</Text>
                <Group gap="md" wrap="wrap">
                  <Group gap={4} wrap="nowrap">
                    <IconUser size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed" truncate>{plan.provider}</Text>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    <IconCalendar size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">Started {plan.startDate}</Text>
                  </Group>
                </Group>
              </Stack>
            </Group>
            <Badge size="sm" variant="light" color={planStatusColor(plan.status)} style={{ flexShrink: 0 }}>
              {plan.status === 'active' ? 'Active' : plan.status}
            </Badge>
          </Group>

          <Stack gap={4}>
            {!plan.diagnosisSections?.length && plan.diagnosis ? (
              <Text size="sm"><Text span fw={600}>Diagnosis: </Text>{plan.diagnosis}</Text>
            ) : null}
            {plan.presentingProblem ? (
              <Text size="sm"><Text span fw={600}>Presenting problem: </Text>{plan.presentingProblem}</Text>
            ) : null}
            {plan.treatmentFrequency ? (
              <Text size="sm"><Text span fw={600}>Frequency: </Text>{plan.treatmentFrequency}</Text>
            ) : null}
            {plan.assignedAt ? (
              <Text size="sm"><Text span fw={600}>Assigned: </Text>{formatUsDateShort(plan.assignedAt)}</Text>
            ) : null}
            {plan.expiresAt ? (
              <Text size="sm"><Text span fw={600}>Expires: </Text>{formatUsDateShort(plan.expiresAt)}</Text>
            ) : null}
            {!plan.diagnosisSections?.length && plan.behavioralDefinitions?.length ? (
              <Stack gap={2}>
                <Text size="sm" fw={600}>Symptoms / behavioral definitions</Text>
                {plan.behavioralDefinitions.map((definition, index) => (
                  <Text key={`${plan.id}-symptom-${index}`} size="sm">• {definition}</Text>
                ))}
              </Stack>
            ) : null}
          </Stack>

          {plan.diagnosisSections?.length ? (
            plan.diagnosisSections.map((section, index) => (
              <Stack key={`${plan.id}-diagnosis-${index}`} gap="xs">
                {section.diagnosis ? (
                  <Text size="sm"><Text span fw={600}>Diagnosis: </Text>{section.diagnosis}</Text>
                ) : null}
                {section.behavioralDefinitions?.length ? (
                  <Stack gap={2}>
                    <Text size="sm" fw={600}>Symptoms / behavioral definitions</Text>
                    {section.behavioralDefinitions.map((definition, symptomIndex) => (
                      <Text key={`${plan.id}-diagnosis-${index}-symptom-${symptomIndex}`} size="sm">
                        • {definition}
                      </Text>
                    ))}
                  </Stack>
                ) : null}
                <GoalsSection goals={section.goals} />
              </Stack>
            ))
          ) : (
            <GoalsSection goals={plan.goals} />
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export function TreatmentPlansTab({
  plans,
  loading = false,
  error = null,
  groupMode = false,
  participantOptions = [],
  selectedPatientId,
  onSelectedPatientChange,
  selectedPatientName,
}: TreatmentPlansTabProps): JSX.Element {
  const loadingMessage = groupMode && selectedPatientName
    ? `Loading treatment plan for ${selectedPatientName}…`
    : 'Loading treatment plan…';
  const emptyMessage = groupMode
    ? 'No active treatment plan for this client’s group episode.'
    : 'No active treatment plan for this episode.';

  if (!groupMode) {
    return (
      <PlansBody
        plans={plans}
        loading={loading}
        error={error}
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
      />
    );
  }

  return (
    <Stack gap="md">
      <TreatmentPlanClientSelect
        options={participantOptions}
        selectedPatientId={selectedPatientId}
        onSelectedPatientChange={onSelectedPatientChange ?? (() => undefined)}
      />
      <Box key={selectedPatientId ?? 'none'}>
        <PlansBody
          plans={plans}
          loading={loading}
          error={error}
          emptyMessage={emptyMessage}
          loadingMessage={loadingMessage}
        />
      </Box>
    </Stack>
  );
}
